import { DotMatrix } from '@clickguard/design-system'
/**
 * VerificationModal — DEMO / REFERENCE ONLY
 *
 * This is NOT exported from the design system. It exists solely as a
 * Storybook reference to show how StatusModal can be composed for the
 * tracking verification flow. The main app should build its own version
 * using StatusModal directly.
 */
import React, { forwardRef } from 'react'
import { StatusModal } from 'components/delete-after-design-package-installation/StatusModal'

// StatusModalAction type — duplicated here to avoid type-only re-export issues with webpack/TS 4.3
interface StatusModalAction {
	label: string
	variant?:
		| 'primary'
		| 'ghost'
		| 'danger'
		| 'secondary'
		| 'blue'
		| 'navy'
		| 'white'
	onClick?: () => void
	loading?: boolean
}

export type VerificationStatus =
	| 'verifying-server'
	| 'server-success'
	| 'server-failed'
	| 'verify-code-ready'
	| 'verifying-code'
	| 'code-success'
	| 'code-failed'

export interface VerificationModalProps {
	/** Whether the modal is visible */
	open: boolean
	/** Current verification step/state */
	status: VerificationStatus
	/** Called when "Go to dashboard" is clicked */
	onGoToDashboard?: () => void
	/** Called when "Contact support" is clicked */
	onContactSupport?: () => void
	/** Called when "Verify" / "Verify again" is clicked to start code verification */
	onVerifyCode?: () => void
	/** Called when modal requests to close */
	onClose?: () => void
	/** Whether navigation is in progress (shows loader on "Go to dashboard" buttons) */
	isNavigating?: boolean
	/** Additional class for the modal content */
	className?: string
}

const HELP_ARTICLE_URL =
	'https://docs.clickguard.com/en/articles/21885-what-is-clickguard-tracking-code'

const REVIEW_CONFIGURATION_PROCESS_URL =
	'https://docs.clickguard.com/en/articles/21547-serverside-tracking-setup-process'

const SuccessIcon: React.FC = () => (
	<div className="cg-flex cg-items-center cg-justify-center cg-h-10 cg-w-10 cg-rounded-full cg-bg-green-50">
		<svg
			className="cg-h-6 cg-w-6 cg-text-green-500"
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={2}
			aria-hidden="true"
		>
			<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
		</svg>
	</div>
)

type StateConfig = {
	icon?: React.ReactNode
	title: string
	color: 'default' | 'success' | 'danger'
	description: React.ReactNode
	actions: StatusModalAction[]
	closable: boolean
}

function getStateConfig(
	status: VerificationStatus,
	handlers: {
		onGoToDashboard?: () => void
		onContactSupport?: () => void
		onVerifyCode?: () => void
	},
	options?: { isNavigating?: boolean }
): StateConfig {
	switch (status) {
		case 'verifying-server':
			return {
				icon: <DotMatrix size="md" label="Verifying..." />,
				title: 'Verifying...',
				color: 'default',
				description:
					'Checking your server-side tracking configuration. This may take a moment.',
				actions: [],
				closable: false
			}

		case 'server-success':
			return {
				icon: <SuccessIcon />,
				title: 'Server-side tracking verified',
				color: 'success',
				description:
					'Your server-side tracking is configured correctly. Proceeding to verify your tracking code installation...',
				actions: [],
				closable: false
			}

		case 'server-failed':
			return {
				title: 'Verification Failed',
				color: 'danger',
				description: (
					<>
						Server-side tracking requires a DNS change, which can take up to 30
						minutes to propagate. We&rsquo;ll keep checking in the background
						and update your status automatically once it&rsquo;s confirmed.
						<br />
						<br />
						If it&rsquo;s still showing as unverified after 30 minutes,{' '}
						<a
							href={REVIEW_CONFIGURATION_PROCESS_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="cg-text-primary-500 cg-underline hover:cg-text-primary-700 cg-transition-colors"
						>
							review the configuration process
						</a>{' '}
						or contact support and we&rsquo;ll help you out.
					</>
				),
				actions: [
					{
						label: 'Contact support',
						variant: 'ghost' as const,
						onClick: handlers.onContactSupport
					},
					{
						label: 'Go to dashboard',
						loading: options?.isNavigating,
						variant: 'primary' as const,
						onClick: handlers.onGoToDashboard
					}
				],
				closable: true
			}

		case 'verify-code-ready':
			return {
				title: 'Tracking code verification',
				color: 'default',
				description:
					'Click the button below to be redirected to your website. We\u2019ll automatically test the tracking code installation\u2014just return to this page once it finishes loading.',
				actions: [
					{
						label: 'Verify installation',
						variant: 'primary' as const,
						onClick: handlers.onVerifyCode
					}
				],
				closable: true
			}

		case 'verifying-code':
			return {
				icon: (
					<DotMatrix size="md" label="Verifying installation" />
				),
				title: 'Verifying tracking code installation...',
				color: 'default',
				description:
					'Please wait a few seconds while we verify your installation...',
				actions: [],
				closable: false
			}

		case 'code-success':
			return {
				icon: <SuccessIcon />,
				title: 'Tracking code verified!',
				color: 'success',
				description:
					'Your tracking code has been verified and is working correctly.',
				actions: [
					{
						label: 'Go to dashboard',
						loading: options?.isNavigating,
						variant: 'primary' as const,
						onClick: handlers.onGoToDashboard
					}
				],
				closable: true
			}

		case 'code-failed':
			return {
				title: 'Verification Failed',
				color: 'danger',
				description: (
					<>
						It looks like the tracking code isn&rsquo;t installed on your
						website yet. Please{' '}
						<a
							href={HELP_ARTICLE_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="cg-text-primary-500 cg-underline hover:cg-text-primary-700 cg-transition-colors"
						>
							review the installation
						</a>{' '}
						and click below to verify again, or reach out to our support team
						for assistance.
					</>
				),
				actions: [
					{
						label: 'Verify again',
						variant: 'ghost' as const,
						onClick: handlers.onVerifyCode
					},
					{
						label: 'Contact support',
						variant: 'ghost' as const,
						onClick: handlers.onContactSupport
					},
					{
						label: 'Go to dashboard',
						loading: options?.isNavigating,
						variant: 'primary' as const,
						onClick: handlers.onGoToDashboard
					}
				],
				closable: true
			}
	}
}

const VerificationModal = forwardRef<HTMLDivElement, VerificationModalProps>(
	(
		{
			open,
			status,
			onGoToDashboard,
			onContactSupport,
			onVerifyCode,
			onClose,
			isNavigating,
			className
		},
		ref
	) => {
		const config = getStateConfig(
			status,
			{ onGoToDashboard, onContactSupport, onVerifyCode },
			{ isNavigating }
		)

		return (
			<StatusModal
				ref={ref}
				open={open}
				onClose={onClose}
				icon={config.icon}
				title={config.title}
				color={config.color}
				description={config.description}
				actions={config.actions}
				closable={config.closable}
				className={className}
			/>
		)
	}
)

VerificationModal.displayName = 'VerificationModal'

export { VerificationModal }
